import { MoreVert } from '@material-ui/icons';
import { useConfirmAndLogEntryDelete } from '@monorepo/logs/src/hooks/useConfirmAndLogEntryDelete';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { DocumentTitle } from '@monorepo/shared/componentsV2/DocumentTitle';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { EditEntryForm } from '@monorepo/shared/componentsV2/logs/EditEntryModal/EditEntryForm';
import { EditLogEntryBuiltInFields } from '@monorepo/shared/componentsV2/logs/EditLogEntryModal/EditLogEntryBuiltInFields';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { Menu } from '@monorepo/shared/componentsV2/menus/Menu';
import { SecondaryBreadcrumbToolbar } from '@monorepo/shared/componentsV2/SecondaryBreadcrumbToolbar';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useConfirmBypassValidationAndSaveEntry } from '@monorepo/shared/hooks/logs/useConfirmBypassValidationAndSaveEntry';
import { useLog } from '@monorepo/shared/hooks/logs/useLog';
import { useLogEntry } from '@monorepo/shared/hooks/logs/useLogEntry';
import { useLogEntryUpdate } from '@monorepo/shared/hooks/logs/useLogEntryUpdate';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import { useLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import {
  contentPadding,
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { format } from 'date-fns';
import {
  localEquivalentOfUTC,
  LogEntryResponse,
  SaveFieldValueRequest,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled(Main)`
  ${verticalLayout}
`;

const FormContainer = styled.div`
  ${fillHeightAndScrollable}
`;

const FormContent = styled.div`
  ${contentPadding}
  ${verticalLayout}
`;

export function SingleLogEntryPage() {
  const { organizationId, projectId, logId, logEntryId } = useParams();
  if (!logId || !logEntryId || !organizationId || !projectId) {
    throw new Error(
      "SingleLogEntryPage was rendered with a url that doesn't have logId, logEntryId, organizationId or projectId",
    );
  }
  const { log, isLoading: isLoadingLog } = useLog({ organizationId, logId });
  const { logFields, isLoading: isLoadingLogFields } = useLogEntryFields({
    organizationId,
    logId,
  });
  const { logEntry, isLoading: isLoadingLogEntry } = useLogEntry({
    organizationId,
    logId,
    logEntryId,
    projectId,
  });

  const formattedLogDate = useMemo(
    () =>
      logEntry &&
      format(localEquivalentOfUTC(new Date(logEntry.logDate)), 'Pp'),
    [logEntry],
  );
  const allRecordsLink = useLinkOnSameProject(`/logs/${logId}/records`);
  const crumbs = useMemo(
    () => [
      { label: 'All Records', to: allRecordsLink },
      {
        label: `Edit Log Entry for ${formattedLogDate}`,
        to: '.',
      },
    ],
    [allRecordsLink, formattedLogDate],
  );

  const [updator] = useLogEntryUpdate({ config: { throwOnError: true } });
  const saver = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formValues: Record<string, any>,
      fieldValues: SaveFieldValueRequest[],
    ) => {
      const updatedLogEntry = {
        // log date is a required field in the form, so it will never be null
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        logDate: UTCEquivalentOfLocal(formValues.logDate)!,
        logFieldValues: fieldValues,
      };
      return updator({
        organizationId,
        projectId,
        logId,
        logEntryId,
        logEntry: updatedLogEntry,
      });
    },
    [updator, organizationId, projectId, logId, logEntryId],
  );
  const { handleSave, persistValidatedForm } =
    useConfirmBypassValidationAndSaveEntry<LogEntryResponse>({
      entryId: logEntryId,
      entryType: 'logEntry',
      fields: logFields,
      saver,
    });

  const navigate = useNavigate();
  const onAfterDelete = useCallback(
    () => navigate(allRecordsLink),
    [navigate, allRecordsLink],
  );
  const { handleDelete } = useConfirmAndLogEntryDelete({
    logDate: logEntry?.logDate,
    logEntryId,
    logId,
    onAfterDelete,
    organizationId,
    projectId,
  });

  if (isLoadingLog || isLoadingLogFields || isLoadingLogEntry) {
    return <Loading />;
  }

  if (!log || !logFields || !logEntry) {
    // We should never get here, TS doesn't know that when all loading states are false we should have data loaded.
    // TS also doesn't know that if loading fails, we throw and error boundary will render a default fallback component instead.
    return null;
  }

  return (
    <Form
      onSubmit={persistValidatedForm}
      subscription={{
        errors: true,
        invalid: true,
        pristine: true,
        submitting: true,
        values: true,
      }}
      // initial values are set on the specific fields; it's more straightforward since the shape of this
      //  form is determined by log config
    >
      {({ errors, handleSubmit, invalid, pristine, submitting, values }) => (
        <Container>
          <DocumentTitle title={['Edit Log entry', log.name]} />
          <SecondaryBreadcrumbToolbar
            crumbs={crumbs}
            rightActions={
              <ButtonsGroup>
                <Button
                  color="primary"
                  disabled={submitting || pristine}
                  onClick={() =>
                    handleSave({ invalid, errors, values, handleSubmit })
                  }
                  variant="contained"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </Button>
                <Menu
                  options={[
                    {
                      onClick: handleDelete,
                      displayName: 'Delete',
                      color: 'danger',
                    },
                  ]}
                  buttonElement={
                    <IconButton>
                      <MoreVert />
                      <VisuallyHidden>Other Log entry actions</VisuallyHidden>
                    </IconButton>
                  }
                />
              </ButtonsGroup>
            }
          />
          <FormContainer>
            <FormContent>
              <EditEntryForm
                entryTypeDisplayName="Log Entry"
                fieldValues={logEntry.logFieldValues}
                fields={logFields}
                instructions={log.instructions}
                organizationId={organizationId}
                projectId={projectId}
              >
                <EditLogEntryBuiltInFields existingLogEntry={logEntry} />
              </EditEntryForm>
            </FormContent>
          </FormContainer>
        </Container>
      )}
    </Form>
  );
}
